import { APPLICATION_ROUTES } from '@/constants/constants';
import queryKeys from '@/constants/queryKeys';
import { UploadTemplateFormData, useAddNewTemplateMutation } from '@/hooks/api/template-gallery/useAddNewTemplateMutation';
import useGetContextQuery from '@/hooks/api/useGetContextQuery';
import { useTemplateGalleryContext } from '@/hooks/useTemplateGalleryContext';
import { openLinkInTab, showToast } from '@/utils/monday';
import { AxiosError } from 'axios';
import { Button } from '@vibe/core';
import { useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

type Props = {
  envelopeId: string;
};

const UploadTemplateButton = ({ envelopeId }: Props) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { hideGallery } = useTemplateGalleryContext();
  const { mutateAsync: addNewTemplate } = useAddNewTemplateMutation();
  const [isLoading, setIsLoading] = useState(false);
  const { data: context } = useGetContextQuery();
  const itemId = context?.itemId;

  const handleAddNewTemplate = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!itemId) {
      console.error('Missing required context data');
      return;
    }

    setIsLoading(true);
    const formData = new FormData() as UploadTemplateFormData;

    formData.append('file', e.target.files?.[0] as File);
    formData.append('envelopeId', envelopeId);
    formData.append('itemId', itemId.toString());
    try {
      const data = await addNewTemplate(formData);
      const sessionToken = queryClient.getQueryData<{ data: string }>(
        queryKeys.MONDAY_GET_SESSION_TOKEN,
      );
      const { _id: templateId, fileId } = data;
      const url = APPLICATION_ROUTES.TEMPLATE_GALLERY_EDITOR({
        templateId,
        fileId,
        context,
        sessionToken: sessionToken?.data ?? '',
      });
      await queryClient.invalidateQueries(queryKeys.GET_FILES_ITEM);
      hideGallery();
      await openLinkInTab({ url });
    } catch (error) {
      console.error(error);
      const axiosError = error as AxiosError<{ message?: string }>;
      await showToast({
        message:
          axiosError.response?.data?.message || 'Failed to upload template',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      size={Button.sizes.SMALL}
      className="mr-8"
      onClick={() => inputFileRef?.current?.click()}
      loading={isLoading}
    >
      <input
        accept=".pdf, .docx"
        hidden
        aria-hidden
        className="hidden"
        type="file"
        onChange={handleAddNewTemplate}
        ref={inputFileRef}
      />
      Upload Template
    </Button>
  );
};

export default UploadTemplateButton;
